import React, { useCallback, useMemo } from 'react';
import { FormattedDate, FormattedTime } from 'react-intl';
import classNames from 'classnames';

import { useMessage } from '@tmapy/intl';
import { useSelector } from '@tmapy/redux';

import { SvgLock, Icon } from '@tmapy/style-guide';

import './DocRow.sass';
import './DocList.scss';

import type { DocListResponse } from '../../openapi/types';
import { PrejimkaTypesEnum } from '../../openapi/types/PrejimkaTypesEnum';

import { RouteId } from '../../routes';
import { useHistoryAction, useAppContext } from '../../hooks';

import { APP_TYPE } from '../../constants';
import { getCmdCtrlUrl } from '../../utils/getcmdctrlurl';
import { documentationTypesMessages, prejimkaTypesMessages } from './docsMessages';
import { DocBadge } from './DocBadge';
import { AttachmentIcon } from './AttachmentIcon';
import { ResponsiblePersonCell } from './ResponsiblePersonColumn';
import {
  DOC_STATE,
  HISTORY_CLOSED,
  OWNER_FLOWS,
  usableFilterItemsForAppType,
  WORKER_FLOWS,
} from '../constants';
import { getDocumentStates } from './DocList';

const docRouteId: Record<PrejimkaTypesEnum, RouteId> = {
  [PrejimkaTypesEnum.CHECK]: 'docCheck',
  [PrejimkaTypesEnum.DMVS_GAD_CHECK]: 'docCheck',
  [PrejimkaTypesEnum.INITIAL_IMPORT]: 'docInitialImport',
  [PrejimkaTypesEnum.DTM]: 'docPodaniDTI',
  [PrejimkaTypesEnum.GP]: 'docDetail',
  [PrejimkaTypesEnum.DMVS_DTI]: 'dmvsDtiDetail',
  [PrejimkaTypesEnum.DMVS_GAD]: 'dmvsGadDetail',
  [PrejimkaTypesEnum.CEPRO_IMPORT]: 'ceproDocDetail',
  [PrejimkaTypesEnum.BB_IMPORT]: 'bbDocDetail',
  [PrejimkaTypesEnum.DTM_KAD]: 'podaniKADDetail',
};

export const DocListItemDefault: React.FC<DocListResponse & { editable?: boolean }> = (props) => {
  const { appType } = useAppContext();
  const routeToDoc = useHistoryAction(docRouteId[props.prejimka_type], { appType, id: props.id });
  const formatMessage = useMessage();
  const customerType = useSelector((state) => state.app.config.customerType);
  const usableItems = usableFilterItemsForAppType[customerType];

  const handleClick = useCallback(
    (event: React.MouseEvent) => {
      if (event.ctrlKey || event.metaKey) {
        window.open(
          getCmdCtrlUrl(docRouteId[props.prejimka_type], { appType, id: props.id }),
          '_blank',
        );
        return; // Ponech nativní chování
      }

      const selectText = window.getSelection();
      if (!selectText || selectText.toString() === '') {
        routeToDoc();
      }
    },
    [routeToDoc],
  );

  const creator = `${props.creator?.name ?? ''} ${props.creator?.surname ?? ''}`.trim();
  const subjekt = `${props.subjekt?.jmeno ?? ''}`.trim();
  const receiver = `${props.receiver?.name ?? ''} ${props.receiver?.surname ?? ''}`.trim();

  const editor_manager =
    props.prejimka_type === PrejimkaTypesEnum.DMVS_DTI
      ? (props.editor_subjekt?.jmeno ?? '')
      : `${props.editor_manager?.name ?? ''} ${props.editor_manager?.surname ?? ''}`.trim();

  const historyClosed = useMemo(() => {
    if (!usableItems.includes('history')) return null;

    const closeStates = getDocumentStates(HISTORY_CLOSED, ['CLOSED'] as DOC_STATE[]);
    const filteredHistoryItem = props.history.find((item) => closeStates.includes(item.state));
    return filteredHistoryItem?.updated || null;
  }, [props.history]);

  const isOwner = props.flow && (OWNER_FLOWS as string[]).includes(props.flow);
  const isWorker = props.flow && (WORKER_FLOWS as string[]).includes(props.flow);

  return (
    <tr className='dmx-docRow' onClick={handleClick}>
      <td
        className={classNames({
          'sg-a-ta-c': true,
          'dmx-is-dtm': props.original_id && appType === APP_TYPE.PREJIMKA,
          'dmx-is-owner': isOwner,
          'dmx-is-worker': isWorker,
        })}
      >
        <DocBadge
          docState={props.state}
          flow={props.flow}
          rejectedBySubjekty={props.rejected_by_subjekty}
        />
        {props.id && <div className='sg-u-type-bold'>{props.id}</div>}
      </td>
      {usableItems.includes('documentation_type') && (
        <td className='sg-a-ta-c'>
          {props.documentation_type && (
            <div>{formatMessage.lookup(documentationTypesMessages, props.documentation_type)}</div>
          )}
        </td>
      )}
      <td>
        {props.name && <div className='sg-u-type-bold sg-u-type-base'>{props.name}</div>}
        {props.description && <div className='sg-u-c-body-darkest'>{props.description}</div>}
      </td>
      {usableItems.includes('cadasters') && (
        <td>{props.cadasters.map((cadaster) => cadaster.name).join(', ')}</td>
      )}
      {usableItems.includes('owner') && <td>{props.owner_name}</td>}
      {usableItems.includes('prejimka_type') && (
        <td>{formatMessage.lookup(prejimkaTypesMessages, props.prejimka_type)}</td>
      )}
      <td className='sg-a-ta-r sg-a-ws-nw sg-a-fvn-tn'>
        {props.created && (
          <div>
            <FormattedDate value={new Date(props.created)} />{' '}
            <FormattedTime value={new Date(props.created)} />
          </div>
        )}
        {props.accepted && customerType === 'gp' && (
          <div className='sg-u-c-cta-black'>
            <FormattedDate value={new Date(props.accepted)} />{' '}
            <FormattedTime value={new Date(props.accepted)} />
          </div>
        )}
        {usableItems.includes('history') && historyClosed && (
          <div className='dmx-u-c-cta-red'>
            <FormattedDate value={new Date(historyClosed)} />{' '}
            <FormattedTime value={new Date(historyClosed)} />
          </div>
        )}
        {customerType === 'gp' && !!props.original_id && props.closed && (
          <div className='dmx-u-c-cta-blue'>
            <FormattedDate value={new Date(props.closed)} />{' '}
            <FormattedTime value={new Date(props.closed)} />
          </div>
        )}
      </td>
      {usableItems.includes('dmvs_id_podani') && <td>{props.dmvs_id_podani || '-'}</td>}
      {usableItems.includes('dmvs_source') && <td>{props.dmvs_source || '-'}</td>}
      {usableItems.includes('creatorFullName') && (
        <td>{props.prejimka_type === 'DMVS_DTI' ? subjekt : creator}</td>
      )}
      {usableItems.includes('receiverFullName') && <td>{receiver}</td>}
      {usableItems.includes('editorManagerFullName') && <td>{editor_manager}</td>}
      <ResponsiblePersonCell responsible_person={props.responsible_person} />
      <td className='sg-a-w-0'>
        {props.attachments_count > 0 && <AttachmentIcon count={props.attachments_count} />}
      </td>
      <td className='sg-a-w-0'>
        {customerType === 'gp' && !props.editable && props.original_id && (
          <div className='dmx-lockIcon'>
            <Icon element={<SvgLock />} size='med' />
          </div>
        )}
      </td>
    </tr>
  );
};
